import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBTypography,
} from "mdbreact";
import { isEqual } from "lodash";
import { useToasts } from "react-toast-notifications";
import {
  SAVE,
  TOGGLE,
  UPDATE,
} from "../../../../../services/redux/slices/market/machines";
import { BROWSE as BROWSE_COMPANIES } from "../../../../../services/redux/slices/assets/companies";

const defaultForm = {
  brand: "",
  model: "",
  serial: "",
  descriptions: "",
  acquired: "brand-new",
  status: "fully functional",
  category: "machines",
  owwnersship: "tieup",
  lisCapable: false,
  warranty: "",
  section: "",
  location: "",
  price: 0,
  mortgage: 0,
  depreciation: 0,
  amortization: 0,
  lastPMDate: "",
  conditionNotes: "",
  pm: {
    value: 1,
    unit: "month",
  },
};

export default function MachineModal({ mode = "registry" }) {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { token, auth, activePlatform } = useSelector(
    ({ auth }) => auth
  );
  const { collections: companies = [] } = useSelector(
    ({ companies }) => companies
  );
  const { showModal, selected, willCreate, isLoading, tagOnly } = useSelector(
    ({ machines }) => machines
  );
  const [form, setForm] = useState(defaultForm);
  const currentSupplierId = activePlatform?.branchId;
  const currentSupplierCompanyId =
    activePlatform?.company?._id || activePlatform?.branch?.companyId?._id || "";

  useEffect(() => {
    if (token && showModal && companies.length === 0) {
      dispatch(BROWSE_COMPANIES({ token }));
    }
  }, [companies.length, dispatch, showModal, token]);

  const companyOptions = useMemo(() => {
    return (Array.isArray(companies) ? companies : []).filter((company) => {
      const companyId = String(company?._id || "");
      return (
        companyId &&
        company?.category !== "supplier" &&
        companyId !== String(currentSupplierCompanyId || "")
      );
    });
  }, [companies, currentSupplierCompanyId]);

  const branchOptions = useMemo(() => {
    return companyOptions.flatMap((company) =>
      (Array.isArray(company?.branches) ? company.branches : [])
        .map((branch) => ({
          ...branch,
          companyRefId: String(company?._id || ""),
          companyName: company?.name || "",
          label: company?.name
            ? `${company.name} -> ${branch?.name || branch?.displayname || branch?._id}`
            : branch?.name || branch?.displayname || branch?._id,
        }))
        .filter(
          (branch) =>
            branch?._id &&
            String(branch?._id) !== String(currentSupplierId || "")
        )
    );
  }, [companyOptions, currentSupplierId]);

  const selectedCompanyId = useMemo(() => {
    if (form.companyRefId) return String(form.companyRefId);
    const matchedBranch = branchOptions.find(
      (branch) => String(branch._id) === String(form.branch || "")
    );
    return String(matchedBranch?.companyRefId || "");
  }, [branchOptions, form.branch, form.companyRefId]);

  const filteredBranchOptions = useMemo(() => {
    if (!selectedCompanyId) return branchOptions;
    return branchOptions.filter(
      (branch) => String(branch.companyRefId) === String(selectedCompanyId)
    );
  }, [branchOptions, selectedCompanyId]);

  const hydratedSelected = useMemo(
    () => ({
      ...defaultForm,
      ...selected,
      companyRefId: "",
      pm: {
        ...defaultForm.pm,
        ...(selected?.pm || {}),
      },
    }),
    [selected]
  );

  useEffect(() => {
    setForm(willCreate ? defaultForm : hydratedSelected);
  }, [hydratedSelected, willCreate]);

  const handleClose = () => dispatch(TOGGLE());

  const applyMachineDefaults = (payload = {}, isStock = false) => ({
    ...payload,
    category: "machines",
    owwnersship: "tieup",
    eId: auth?._id,
    branch: isStock ? payload?.branch || undefined : activePlatform?.branchId,
    suppllier: activePlatform?.branchId,
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!willCreate && !tagOnly && isEqual(form, hydratedSelected)) {
      addToast("No changes found.", { appearance: "info" });
      return;
    }

    const isStockRecord = mode === "stock";
    const isTieupRecord = mode === "tieup";
    const payload = applyMachineDefaults(form, isStockRecord);

    if ((isTieupRecord || tagOnly) && !payload.branch) {
      addToast("Select a lab branch for this tieup machine.", {
        appearance: "warning",
      });
      return;
    }

    if (willCreate) {
      dispatch(SAVE({ data: payload, token }));
      return;
    }

    dispatch(
      UPDATE({
        data: { ...payload, _id: hydratedSelected?._id },
        token,
      })
    );
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setCompany = (companyRefId) =>
    setForm((prev) => ({
      ...prev,
      companyRefId,
      branch: "",
    }));
  const setPmField = (key, value) =>
    setForm((prev) => ({
      ...prev,
      pm: {
        ...(prev.pm || defaultForm.pm),
        [key]: value,
      },
    }));

  return (
    <MDBModal isOpen={showModal} toggle={handleClose} size="lg" backdrop>
      <MDBModalHeader toggle={handleClose} className="light-blue darken-3 white-text">
        <MDBIcon icon="microscope" className="mr-2" />
        {tagOnly
          ? "Tag supplier machine"
          : willCreate
          ? "Add supplier machine"
          : "Update supplier machine"}
      </MDBModalHeader>
      <MDBModalBody>
        <form onSubmit={handleSubmit}>
          <MDBTypography tag="h5" className="text-center mb-4">
            {tagOnly
              ? "Assign this supplier machine to a company and branch"
              : "Fixed Asset Entry for Supplier-Owned Machine"}
          </MDBTypography>
          <div className="row">
            {tagOnly ? (
              <>
                <div className="col-md-12 mb-3">
                  <label className="grey-text">Company</label>
                  <select
                    className="form-control"
                    value={selectedCompanyId}
                    onChange={(e) => setCompany(e.target.value)}
                  >
                    <option value="">Select company</option>
                    {companyOptions.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-12 mb-3">
                  <label className="grey-text">Branch</label>
                  <select
                    className="form-control"
                    value={form.branch || ""}
                    onChange={(e) => setField("branch", e.target.value || undefined)}
                  >
                    <option value="">Select branch</option>
                    {filteredBranchOptions.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name || branch.displayname || branch._id}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
            <div className="col-md-6">
              <MDBInput
                label="Brand"
                value={form.brand}
                onChange={(e) => setField("brand", e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <MDBInput
                label="Model"
                value={form.model}
                onChange={(e) => setField("model", e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <MDBInput
                label="Serial"
                value={form.serial}
                onChange={(e) => setField("serial", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <MDBInput
                label="Warranty expiry"
                type="date"
                value={form.warranty || ""}
                onChange={(e) => setField("warranty", e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="grey-text">Acquired condition</label>
              <select
                className="form-control"
                value={form.acquired}
                onChange={(e) => setField("acquired", e.target.value)}
              >
                <option value="brand-new">Brand new</option>
                <option value="refurbish">Refurbish</option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="grey-text">Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                <option value="fully functional">Fully functional</option>
                <option value="functional">Functional</option>
                <option value="damaged">Damaged</option>
                <option value="broken">Broken</option>
              </select>
            </div>
            <div className="col-md-6">
              <MDBInput
                label="Section"
                value={form.section}
                onChange={(e) => setField("section", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <MDBInput
                label="Location"
                value={form.location}
                onChange={(e) => setField("location", e.target.value)}
              />
            </div>
            {(mode === "stock" || mode === "tieup") && (
              <div className="col-md-12 mb-3">
                <label className="grey-text">Target lab</label>
                <select
                  className="form-control"
                  value={form.branch || ""}
                  onChange={(e) => setField("branch", e.target.value || undefined)}
                >
                  <option value="">
                    {mode === "stock" ? "Unassigned stock" : "Select lab branch"}
                  </option>
                  {branchOptions.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-4">
              <MDBInput
                label="Price"
                type="number"
                value={form.price}
                onChange={(e) => setField("price", Number(e.target.value || 0))}
              />
            </div>
            <div className="col-md-4">
              <MDBInput
                label="PM value"
                type="number"
                value={form.pm?.value || 1}
                onChange={(e) => setPmField("value", Number(e.target.value || 1))}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="grey-text">PM unit</label>
              <select
                className="form-control"
                value={form.pm?.unit || "month"}
                onChange={(e) => setPmField("unit", e.target.value)}
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
            <div className="col-md-6">
              <MDBInput
                label="Last PM date"
                type="date"
                value={form.lastPMDate || ""}
                onChange={(e) => setField("lastPMDate", e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3 d-flex align-items-center">
              <div className="custom-control custom-switch mt-4">
                <input
                  id="lis-capable"
                  type="checkbox"
                  className="custom-control-input"
                  checked={Boolean(form.lisCapable)}
                  onChange={(e) => setField("lisCapable", e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="lis-capable">
                  LIS capable
                </label>
              </div>
            </div>
            <div className="col-12">
              <MDBInput
                type="textarea"
                rows="3"
                label="Description"
                value={form.descriptions}
                onChange={(e) => setField("descriptions", e.target.value)}
              />
            </div>
            <div className="col-12">
              <MDBInput
                type="textarea"
                rows="3"
                label="Condition notes"
                value={form.conditionNotes}
                onChange={(e) => setField("conditionNotes", e.target.value)}
              />
            </div>
              </>
            )}
          </div>
          <div className="text-center mt-3">
            <MDBBtn type="submit" color="info" rounded disabled={isLoading}>
              {tagOnly
                ? "Tag machine"
                : willCreate
                ? "Save machine"
                : "Update machine"}
            </MDBBtn>
          </div>
        </form>
      </MDBModalBody>
    </MDBModal>
  );
}
